import { z, ZodObject, ZodRawShape, RefinementCtx } from 'zod';
import { HEALTH_QUESTIONS, STEP_PHQ_1_ERRORS, PLEASE_SELECT_LEAST } from 'src/features/onboarding/components/6-health-question-form/constants';

export function getHealthStepSchemaFromText(stepId: string): ZodObject<ZodRawShape> {
  const step = HEALTH_QUESTIONS.find((q) => q.id === stepId);

  if (!step) return z.object({});

  if (step.id === 'privacyDisclosure') {
    return z.object({
      healthQuestions: z.object({
        [step.id]: z.object({
          disclaimer: z
            .boolean({
              required_error: 'Please acknowledge the disclaimer.',
            })
            .refine((val) => val === true, {
              message: 'Please accept the disclaimer.',
            }),
          accepted: z
            .boolean()
            .refine((val) => val === true, {
              message: 'Please acknowledge the privacy disclosure.',
            }),
          signature: z
            .string()
            .refine((v) => v.trim().length >= 3, {
              message: 'Minimal length is 3 characters',
            })
        }),
      }),
    });
  }

  switch (step.type) {
    case 'initialQuestions':
      return z.object({
        healthQuestions: z.object({
          [step.id]: z.object({
            treatment: z
              .boolean({
                required_error: STEP_PHQ_1_ERRORS.treatment,
              })
              .refine((val) => val !== undefined, {
                message: STEP_PHQ_1_ERRORS.treatment,
              }),
            invalid: z
              .boolean({
                required_error: STEP_PHQ_1_ERRORS.invalid,
              })
              .refine((val) => val !== undefined, {
                message: STEP_PHQ_1_ERRORS.invalid,
              }),
            pregnancy: z
              .boolean({
                required_error: STEP_PHQ_1_ERRORS.pregnancy,
              })
              .refine((val) => val !== undefined, {
                message: STEP_PHQ_1_ERRORS.pregnancy,
              }),
          }).superRefine((data, ctx) => {
            (['treatment', 'invalid', 'pregnancy'] as const).forEach((field) => {
              if (data[field] === undefined) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [field],
                  message: STEP_PHQ_1_ERRORS[field],
                });
              }
            });
          }),
        }),
      });

    case 'takingMedicationsNow':
      return z.object({
        healthQuestions: z.object({
          [stepId]: z.object({
            value: z.boolean({
              required_error: 'Please select any value.',
            }),
            // medications: z
            //   .array(
            //     z.object({
            //       applicant: z.string().min(1, 'Укажите получателя'),
            //       dosage: z.string().min(1, 'Укажите дозировку'),
            //       frequency: z.string().min(1, 'Укажите частоту'),
            //     })
            //   )
            //   .optional()
            //   .refine(
            //     (medications, ctx) => {
            //       // Если value === true, medications должен быть массивом с хотя бы одним элементом
            //       if (ctx.parent.value === true && (!medications || medications.length === 0)) {
            //         return false;
            //       }
            //       return true;
            //     },
            //     {
            //       message: 'Пожалуйста, укажите хотя бы одно лекарство',
            //       path: ['medications'],
            //     }
            //   ),
          }),
        }),
      });

    case 'checkbox':
      return z.object({
        healthQuestions: z.object({
          [step.id]: z.object({
            conditions: z
              .array(z.object({ value: z.string() }))
              .min(1, { message: PLEASE_SELECT_LEAST })
              .optional(),
          }),
        }),
      });

    case 'date':
      return z.object({
        healthQuestions: z.object({
          [step.id]: z.object({
            effectiveDate: z
              .string({ required_error: 'Please select effective date.' })
              .min(1, 'Start date is required.'),
          }),
        }),
      });

    case 'disclaimer':
      return z.object({
        healthQuestions: z.object({
          [step.id]: z.object({
            accepted: z.boolean({
              required_error: 'Please accept the disclaimer.',
            }),
          }),
        }),
      });

    case 'info':
      return z.object({});

    case 'medication':
      return z.object({
        healthQuestions: z.object({
          [step.id]: z.object({
            medications: z
              .array(
                z.object({
                  id: z.string().min(1, { message: 'Medication id is required.' }),
                  dosageId: z.string().optional(),
                  dosageDescription: z.string().optional(),
                  frequency: z.string().min(1, { message: 'Frequency is required.' }),
                  frequencyDescription: z.string().optional(),
                  applicant: z.string().min(1, { message: 'Recipient is required.' }),
                  description: z.string().optional(),
                })
                .superRefine((medication, ctx) => {
                  // dosageDescription is required if dosageId is not provided
                  // Check for empty string or falsy value
                  const hasDosageDescription = medication.dosageDescription && medication.dosageDescription.trim().length > 0;
                  if (!medication.dosageId && !hasDosageDescription) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: ['dosageDescription'],
                      message: 'Dosage is required.',
                    });
                  }
                  
                  // frequencyDescription is required for "Every X hours" or "As needed"
                  // Check for empty string or falsy value
                  const hasFrequencyDescription = medication.frequencyDescription && medication.frequencyDescription.trim().length > 0;
                  if (
                    (medication.frequency === 'Every X hours' || medication.frequency === 'As needed') &&
                    !hasFrequencyDescription
                  ) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: ['frequencyDescription'],
                      message: 'Frequency description is required.',
                    });
                  }
                })
              )
              .optional(),
          }),
        }),
      });

    default:
      return z.object({});
  }
}
